import { create } from 'zustand'

import {
  getAllCertificates,
  createCertificate as pbCreateCertificate,
  revokeCertificate,
} from '../api/certificatesService'

import { useCourseStore } from './courseStore'
import { useSessionStore } from './sessionStore'
import { useAttendeeStore } from './attendeeStore'
import type { Certificate } from './types'

function generateId(): string {
  return crypto.randomUUID()
}

function generateCertNumber(sequence: number): string {
  const year = new Date().getFullYear()
  return `UBS-${year}-${String(sequence).padStart(5, '0')}`
}

function addYears(dateStr: string, years: number): string {
  const d = new Date(dateStr)
  d.setFullYear(d.getFullYear() + years)
  return d.toISOString().slice(0, 10)
}

export function getCertificateStatus(cert: Certificate): 'valid' | 'expired' | 'revoked' {
  if (cert.revoked) {return 'revoked'}
  if (new Date(cert.expiresAt) < new Date()) {return 'expired'}
  return 'valid'
}

interface CertificateState {
  certificates: Certificate[]
  _certSequence: number

  loadCertificates: () => Promise<void>
  generateCertificate: (attendeeId: string) => Promise<Certificate>
  revokeCertificate: (certificateId: string, opts: { revoked: boolean; reason?: string }) => Promise<void>
  getCertificateByToken: (token: string) => Certificate | null
  getAttendeeCertificate: (attendeeId: string) => Certificate | null
}

export const useCertificateStore = create<CertificateState>((set, get) => ({
  certificates: [],
  _certSequence: 1,

  loadCertificates: async () => {
    try {
      const pbCerts = await getAllCertificates()
      const certificates: Certificate[] = pbCerts.map((c) => ({
        id: c.id,
        attendeeId: c.attendeeId,
        certificateNumber: c.certificateNumber,
        issuedAt: c.issuedAt,
        expiresAt: c.expiresAt,
        verificationToken: c.verificationToken,
        revoked: c.revoked,
        revokedAt: c.revokedAt ?? null,
        revokedReason: c.revokedReason ?? '',
        emailSentAt: c.emailSentAt ?? null,
        emailError: c.emailError,
        attendeeDetail: c.attendeeDetail,
      }))
      const maxSeq = certificates.reduce((max, c) => {
        const cn = c.certificateNumber ?? ''
        const match = cn.match(/-(\d+)$/)
        const seq = match ? parseInt(match[1], 10) : 0
        return Math.max(max, seq)
      }, 0)
      set({ certificates, _certSequence: maxSeq + 1 })
    } catch (err) {
      console.error('[CertificateStore] loadCertificates failed:', err)
    }
  },

  generateCertificate: async (attendeeId) => {
    const { attendees } = useAttendeeStore.getState()
    const attendee = attendees.find((a) => a.id === attendeeId)
    if (!attendee) {throw new Error('Attendee not found')}

    const { sessions } = useSessionStore.getState()
    const session = sessions.find((s) => s.id === attendee.sessionId)
    if (!session) {throw new Error('Session not found')}

    const { courses } = useCourseStore.getState()
    const course = courses.find((c) => c.slug === session.courseId)
    if (!course) {throw new Error('Course not found')}

    const today = new Date().toISOString().slice(0, 10)
    const seq = get()._certSequence
    const verificationToken = generateId()

    const pbCert = await pbCreateCertificate({
      attendeeId,
      certificateNumber: generateCertNumber(seq),
      issuedAt: today,
      expiresAt: addYears(today, course.validYears),
      verificationToken,
      revoked: false,
    })

    const cert: Certificate = {
      id: pbCert.id,
      attendeeId: pbCert.attendeeId,
      certificateNumber: pbCert.certificateNumber,
      issuedAt: pbCert.issuedAt,
      expiresAt: pbCert.expiresAt,
      verificationToken: pbCert.verificationToken,
      revoked: pbCert.revoked,
      emailSentAt: pbCert.emailSentAt ?? null,
      emailError: pbCert.emailError,
      attendeeDetail: pbCert.attendeeDetail,
    }
    set((s) => ({ certificates: [...s.certificates, cert], _certSequence: s._certSequence + 1 }))
    return cert
  },

  revokeCertificate: async (certificateId, opts) => {
    await revokeCertificate(certificateId, opts)
    await get().loadCertificates()
  },

  getCertificateByToken: (token) =>
    get().certificates.find((c) => c.verificationToken === token) ?? null,

  getAttendeeCertificate: (attendeeId) =>
    get().certificates.find((c) => c.attendeeId === attendeeId) ?? null,
}))
